import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { RoutingConfigService, RoutingService } from '@spartacus/core';
import { combineLatest, Observable, of, switchMap, take } from 'rxjs';
import { CustomCheckoutStepService } from '../services/checkout-step.service';
import { CustomActiveCartService } from '../../../../cart/_core/active-cart.service';
import { isEmpty } from '../../../../../../core/services/simple-tools';
import { ToasterService } from '../../../../../../components/toaster/toaster.service';
import { TOASTER_TYPE } from '../../../../../../components/toaster/toaster.model';
import { CUSTOM_DELIVERY_METHOD } from '../../../b2b/core/checkout.model';
import { Cart } from '@spartacus/cart/base/root';

@Injectable({
  providedIn: 'root',
})
export class CustomStepValidationGuard {
  constructor(
    protected routingService: RoutingService,
    protected checkoutStepService: CustomCheckoutStepService,
    protected routingConfigService: RoutingConfigService,
    protected activeCartService: CustomActiveCartService,
    protected toasterService: ToasterService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const currentRouteUrl = '/' + route.url.join('/');

    return combineLatest([this.checkoutStepService.steps$, this.activeCartService.getActive()]).pipe(
      take(1),
      switchMap(([steps, cart]) => {
        const currentIndex = steps.findIndex(step => {
          const stepRouteUrl = `/${this.routingConfigService.getRouteConfig(step.routeName)?.paths?.[0]}`;
          return stepRouteUrl === currentRouteUrl;
        });

        if (currentIndex <= 0) {
          // eslint-disable-next-line no-console
          console.error('Undefined Step');
          this.routingService.go(['/cart']);
          return of(false);
        }

        if (currentIndex >= 1) {
          const isOk = this.checkPaymentInformation(cart);

          if (!isOk) {
            this.routingService.go(['/checkout/payment-type']);
            return of(false);
          }
        }

        if (currentIndex >= 2) {
          const isOk = this.checkShippingInformation(cart);

          if (!isOk) {
            this.routingService.go(['/checkout/shipping-address']);
            return of(false);
          }
        }

        if (currentIndex >= 3) {
          const isOk = this.checkShippingPriority(cart);

          if (!isOk) {
            this.routingService.go(['/checkout/delivery-mode']);
            return of(false);
          }
        }

        // http://localhost:4200/checkout/shipping-address
        // http://localhost:4200/checkout/delivery-mode
        // http://localhost:4200/checkout/review-order

        return of(true);
      }),
    );
  }

  checkPaymentInformation(cart: Cart): boolean {
    let isOk = true;

    if (isEmpty(cart.purchaseOrderNumber)) {
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: 'Please enter a <strong>Purchase Order Number</strong>',
      });

      isOk = isOk && false;
    }

    return isOk;
  }

  checkShippingInformation(cart: Cart): boolean {
    let isOk = true;

    if (isEmpty(cart.shippingPreference?.code)) {
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: 'Please select a <strong>Shipping Preference</strong>',
      });

      isOk = isOk && false;
    }

    if (isEmpty(cart.deliveryAddress?.id)) {
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: 'Please select a <strong>Shipping Address</strong>',
      });

      isOk = isOk && false;
    }

    return isOk;
  }

  checkShippingPriority(cart: Cart): boolean {
    let isOk = true;

    if (isEmpty(cart.shippingPriority?.method)) {
      this.toasterService.init({
        type: TOASTER_TYPE.DANGER,
        content: 'Please select a <strong>Delivery Mode</strong>',
      });

      isOk = isOk && false;
    }

    if (cart.shippingPriority?.method === CUSTOM_DELIVERY_METHOD.MARATHON_BEST_WAY) {
      if (isEmpty(cart.shippingPriority.method)) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: 'Please select a <strong>Delivery Method</strong>',
        });

        isOk = isOk && false;
      }
    }

    if (cart.shippingPriority?.method === CUSTOM_DELIVERY_METHOD.CUSTOMER_SPECIFIED_CARRIER) {
      if (isEmpty(cart.shippingPriority.carrier?.code)) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: 'Please select a <strong>Carrier</strong>',
        });

        isOk = isOk && false;
      }

      if (isEmpty(cart.shippingPriority.contractNumber)) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: 'Please enter a <strong>Contract Number</strong>',
        });

        isOk = isOk && false;
      }
    }

    if (cart.shippingPriority?.method === CUSTOM_DELIVERY_METHOD.CUSTOMER_PICKUP) {
      if (isEmpty(cart.shippingPriority?.shippingAttention)) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: 'Please select a <strong>Shipping Attention To</strong>',
        });

        isOk = isOk && false;
      }

      if (isEmpty(cart.shippingPriority.shippingInstructions)) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: 'Please enter a <strong>Shipping Instructions</strong>',
        });

        isOk = isOk && false;
      }

      if (isEmpty(cart.shippingPriority.acknowledgment?.code)) {
        this.toasterService.init({
          type: TOASTER_TYPE.DANGER,
          content: 'Please enter a <strong>Shipping Acknowledgment</strong>',
        });

        isOk = isOk && false;
      }
    }

    return isOk;
  }
}
